import dagre from "dagre"
import _ from "lodash"
import { compact } from "core/utils"
import { log } from "../../debug-log"
import { reconstructGraph } from "./reconstruct-graph"

/**
 * Responsible for calculating actual layout of the graph, mostly by utilizing
 * the dagra graph lib: https://github.com/dagrejs/dagre/wiki
 *
 * It will also insert virtual edges and nodes where placeholders are needed
 * for condition nodes. If a condition node has no children we need virtual
 * nodes and edges present for layout/drawing purposes.
 */
export class Layout {
  static conditionEdgeWeight = 4
  static defaultEdgeWeight = 1
  static maxNodeWidth = 240
  static minNodeHeight = 100
  static minHeight = 500

  static get graphConfig() {
    return {
      nodesep: 65,
      ranksep: 155,
      edgesep: 45,
      marginx: 45,
      marginy: 45
    }
  }

  constructor(nodes = [], edges = [], config = {}) {
    this.nodes = nodes
    this.edges = edges

    this.graph = new dagre.graphlib.Graph({ directed: true })

    const { zoom = 1, ...rest } = config
    const layoutConfig = { ...Layout.graphConfig, ...rest }
    const graphConfig = _.mapValues(layoutConfig, value => value * zoom)
    this.graph.setGraph(graphConfig)

    this.graph.setDefaultEdgeLabel(function() {
      return {}
    })
  }

  get width() {
    return this.graph.graph().width
  }

  get height() {
    return this.graph.graph().height
  }

  layout() {
    log("layout")
    this.nodes.forEach(this.setNode)
    this.edges.forEach(this.setEdge)

    const insertVirtualEdges = _.flow([
      this.getConditions,
      this.rejectPathConditions,
      this.getOutEdges,
      this.getEdgeMetaData,
      this.groupEdges,
      this.rejectCompleteConditionalEdges,
      edges => edges.forEach(this.insertConditionalEdge)
    ])

    insertVirtualEdges(this.nodes)

    this.setupSplitPathNodes()
    this.balanceEntryNodes()

    log("layout", "first pass")

    this.graph = reconstructGraph(this.graph)
    log("layout", "second pass")

    dagre.layout(this.graph)
    log("layout", "finished")

    this.adjustEntryNodes()
  }

  balanceEntryNodes = () => {
    const nodes = this.graph.sources()
    const endNode = this.nodes.find(n => n.kind == "end")
    if (!endNode) return

    // Calculate shortest distance from source nodes to end node
    // https://github.com/dagrejs/graphlib/wiki/API-Reference#alg-dijkstra
    const distances = nodes.reduce((distances, nodeId) => {
      let dijkstra = dagre.graphlib.alg.dijkstra(this.graph, nodeId)
      distances[nodeId] = dijkstra[endNode.id].distance
      return distances
    }, {})

    // Get the longest branch ignoring disconnected nodes (with `Infinity` length)
    const maxDistance = Object.values(distances).reduce(
      (max, current) => (isFinite(current) && current > max ? current : max),
      0
    )

    // Extend the paths with shorter distance so all source nodes end up at the same height
    for (let nodeId of Object.keys(distances)) {
      const distance = distances[nodeId]
      if (distance >= maxDistance) continue
      this.extendEdges(nodeId, maxDistance - distance)
    }
  }

  /**
   * Dagre has no way to force rank for specific nodes.
   * It used to have support but got removed https://github.com/dagrejs/dagre/issues/159
   *
   * This means entry nodes are "heavy" and will not always be positioned at the top rank.
   * `balanceEntryNodes` helps by adding virtual edges, but since it's calculating the shortest distance
   * there will still be cases where it's not adding enough virtual edges.
   * For example, when there's a conditional node with more nodes in one branch than the other.
   *
   * This method adjusts the final computed position of the entry nodes so they are all at the same height.
   */
  adjustEntryNodes = () => {
    const sources = compact(this.graph.sources().map(n => this.graph.node(n)))
    const lowestRankPosition = Math.min(...sources.map(n => n.y))

    // Make all source nodes have the same `y` position
    const changedNodes = []
    for (const node of sources) {
      if (node.y == lowestRankPosition) continue
      changedNodes.push(node)
      node.originalY = node.y
      node.y = lowestRankPosition
      this.graph.setNode(node.id, node)
    }

    // Adjust edges for the nodes that got a new position
    for (const edge of this.graph.edges()) {
      const edgeAttrs = this.graph.edge(edge)
      const node = changedNodes.find(n => n.id == edge.v)
      if (!node) continue
      edgeAttrs.points[0].y -= node.originalY - node.y
    }
  }

  extendEdges = (node, distance) => {
    let edges = this.graph.outEdges(node)
    for (let edge of edges) {
      let previousNode = edge.v
      for (let i = 0; i < distance; i++) {
        const virtualId = `virtual-${edge.v}-${edge.w}-${i}`
        this.setVirtualEdge(previousNode, edge.w, virtualId)
        previousNode = virtualId
      }
    }
  }

  setNode = node => {
    this.graph.setNode(node.id, {
      ...node
    })
  }

  setEdge = edge => {
    const { source, target, ...data } = edge
    const attrs = {
      ...data,
      weight:
        data.condition || data.condition_path
          ? Layout.conditionEdgeWeight
          : Layout.defaultEdgeWeight
    }

    this.graph.setEdge(source, target, attrs)
  }

  getConditions = nodes => {
    return nodes.filter(node => node.kind == "condition")
  }

  rejectPathConditions = nodes => {
    return nodes.filter(node => !node.split_path)
  }

  getOutEdges = nodes => {
    return _.flatMap(nodes, node => this.graph.outEdges(node.id))
  }

  getEdgeMetaData = edges => {
    return edges.map(edge => ({ ...edge, ...this.graph.edge(edge) }))
  }

  groupEdges = edges => {
    return _.groupBy(edges, "v")
  }

  rejectCompleteConditionalEdges = group => {
    return _.reject(group, edges => edges.every(edge => edge.condition))
  }

  insertConditionalEdge = groupedEdges => {
    let virtualEdges = []
    const allowedEdges = ["yes", "no"]
    const unknownEdges = groupedEdges.filter(e => !e.condition)
    const knownEdges = _.compact(groupedEdges.map(e => e.condition))

    if (knownEdges.length) {
      virtualEdges = _.map(unknownEdges, edge => {
        edge.condition = knownEdges.includes("yes") ? "no" : "yes"
        return edge
      })
    } else {
      virtualEdges = _.map(allowedEdges, (direction, index) => {
        const edge = unknownEdges[index] || _.clone(unknownEdges[0])
        edge.condition = direction
        return edge
      })
    }

    virtualEdges.forEach(edge => {
      this.setVirtualConditionalEdge(edge, edge.condition)
    })
  }

  setVirtualConditionalEdge = (edge, condition) => {
    const virtualId = `virtual-${edge.v}-${condition}`

    this.setVirtualNode(virtualId)

    this.graph.setEdge(edge.v, virtualId, {
      weight: Layout.conditionEdgeWeight,
      condition: condition
    })

    this.graph.setEdge(virtualId, edge.w, {
      weight: Layout.conditionEdgeWeight,
      condition: condition
    })

    this.graph.removeEdge(edge.v, edge.w)
  }

  setupSplitPathNodes = () => {
    const splitPathNodes = this.nodes.filter(node => node.split_path)

    for (const node of splitPathNodes) {
      const edges = this.graph.outEdges(node.id).map(edge => {
        return { ...this.graph.edge(edge), ...edge }
      })

      // Remove the default 'nil' edge, which we never render:
      const defaultEdge = edges.find(edge => !edge.condition_path)
      this.graph.removeEdge(defaultEdge.v, defaultEdge.w)

      // Ensure an edge exists for each path:
      for (const path of node.detail.matches_paths) {
        const matchingEdge = edges.find(edge => edge.condition_path == path.id)
        if (matchingEdge) {
          this.replaceSplitPathEdge(matchingEdge, path.id)
        } else {
          this.setVirtualSplitPathEdge(defaultEdge, path.id)
        }
      }

      // Ensure an edge exists for the default "Everyone else" path:
      const matchingDefaultEdge = edges.find(
        edge => edge.condition_path == "true"
      )
      if (matchingDefaultEdge) {
        this.replaceSplitPathEdge(matchingDefaultEdge, "true")
      } else {
        this.setVirtualSplitPathEdge(defaultEdge, "true")
      }
    }
  }

  replaceSplitPathEdge = (edge, conditionPath) => {
    this.graph.removeEdge(edge.v, edge.w)
    this.graph.setEdge(edge.v, edge.w, {
      weight: Layout.conditionEdgeWeight,
      condition_path: conditionPath
    })
  }

  setVirtualSplitPathEdge = (edge, conditionPath) => {
    const virtualId = `virtual-${edge.v}-${conditionPath}`

    this.setVirtualNode(virtualId)

    // For path spacing, it's important that only the first virtual edge has
    // the condition weight applied.
    this.graph.setEdge(edge.v, virtualId, {
      weight: Layout.conditionEdgeWeight,
      condition_path: conditionPath
    })

    this.graph.setEdge(virtualId, edge.w, {
      condition_path: conditionPath
    })
  }

  setVirtualEdge = (source, target, virtualId) => {
    this.setVirtualNode(virtualId)
    this.graph.setEdge(source, virtualId)
    this.graph.setEdge(virtualId, target)
    this.graph.removeEdge(source, target)
  }

  setVirtualNode = id => {
    return this.graph.setNode(id, {
      width: Layout.maxNodeWidth,
      height: Layout.minNodeHeight,
      kind: "virtual",
      element: document.createElement("div")
    })
  }
}
